import type { HekateDisplayForm } from '@onshape/www-inertia';
import { ErrorTagUtil } from '@onshape/www-inertia';
import ErrorUtil from '../../../../util/ErrorUtil';
import type UnifiedSignupSessionInfo from './session-info/UnifiedSignupSessionInfo';
import type { SignupScreen } from './screens/SignupScreen';
import GenericStartSignupScreen from './screens/GenericStartSignupScreen';
import IntentSignupScreen from './screens/IntentSignupScreen';
import PersonaBizIntentSignupScreen from './screens/PersonaBizIntentSignupScreen';
import ProDetails1SignupScreen from './screens/ProDetails1SignupScreen';
import ProDetails2SignupScreen from './screens/ProDetails2SignupScreen';
import StandardDetailsScreen from './screens/StandardDetailsScreen';
import ChoosePlanSignupScreen from './screens/ChoosePlanSignupScreen';
import UnifiedSignupChoosePlanCards from './session-info/UnifiedSignupChoosePlanCards';
import TrialStartSignupScreen from './screens/TrialStartSignupScreen';
import FreeStartSignupScreen from './screens/FreeStartSignupScreen';
import StandardStartSignupScreen from './screens/StandardStartSignupScreen';
import PersonaEduConvergeSignupScreen from './screens/PersonaEduConvergeSignupScreen';
import PersonaHobbyIntentSignupScreen from './screens/PersonaHobbyIntentSignupScreen';
import PersonaEduIntentSignupScreen from './screens/PersonaEduIntentSignupScreen';
import SignupSuccessSignupScreen from './screens/SignupSuccessSignupScreen';
import FreeDetails2SignupScreen from './screens/FreeDetails2SignupScreen';
import FreeDetails1SignupScreen from './screens/FreeDetails1SignupScreen';
import type { OnSubmitFormFunction } from '../../../../../model/OnSubmitFormFunction';
import EduStartSignupScreen from './screens/EduStartSignupScreen';
import LandingStartSignupScreen from './screens/LandingStartSignupScreen';
import EmailStatusSignupScreen from './screens/EmailStatusSignupScreen';
import EmailStatusErrorSignupScreen from './screens/EmailStatusErrorSignupScreen';
import EduDetailsStudentK12SignupScreen from './screens/EduDetailsStudentK12SignupScreen';
import EduDetailsEducatorSignupScreen from './screens/EduDetailsEducatorSignupScreen';
import EduDetailsStudentSignupScreen from './screens/EduDetailsStudentSignupScreen';
import EmailCheck204SignupScreen from './screens/EmailCheck204SignupScreen';
import EmailCheck401SignupScreen from './screens/EmailCheck401SignupScreen';
import EmailCheck422SignupScreen from './screens/EmailCheck422SignupScreen';
import EmailCheck500SignupScreen from './screens/EmailCheck500SignupScreen';
import SignupSubmittingSignupScreen from './screens/SignupSubmittingSignupScreen';
import ErrorOutSignupScreen from './screens/ErrorOutSignupScreen';
import SignupSuccessRedirectSignupScreen from './screens/SignupSuccessRedirectSignupScreen';
import UnifiedSignupUtil from './UnifiedSignupUtil';
import HomePageStartSignupScreen from './screens/HomePageStartSignupScreen';
import HomePageNameSignupScreen from './screens/HomePageNameSignupScreen';
import ErrorAgeUnderThirteenSignupScreen from './screens/ErrorAgeUnderThirteenSignupScreen';

export default class UnifiedSignupScreenProvider {
  private readonly sessionInfo: Ref<UnifiedSignupSessionInfo>;

  private readonly formDefinition: Ref<HekateDisplayForm>;

  private readonly screens: readonly SignupScreen[];

  private readonly onUpdatePage: VoidFunction;

  private readonly onSubmitForm: OnSubmitFormFunction;

  private readonly isBackButtonHidden: Ref<boolean>;

  private readonly submitText: string;

  constructor({
    sessionInfo,
    formDefinition,
    onUpdatePage,
    onSubmitForm,
    isNextButtonHidden,
    isBackButtonHidden,
    isFormSubmitting,
    signupSurveyDiv,
    submitText,
  }: {
    sessionInfo: Ref<UnifiedSignupSessionInfo>;
    formDefinition: Ref<HekateDisplayForm>;
    onUpdatePage: VoidFunction;
    onSubmitForm: OnSubmitFormFunction;
    isNextButtonHidden: Ref<boolean>;
    isBackButtonHidden: Ref<boolean>;
    isFormSubmitting: Ref<boolean>;
    signupSurveyDiv: HTMLDivElement | null;
    submitText: string;
  }) {
    this.sessionInfo = sessionInfo;
    this.formDefinition = formDefinition;
    this.onUpdatePage = onUpdatePage;
    this.onSubmitForm = onSubmitForm;
    this.isBackButtonHidden = isBackButtonHidden;
    this.submitText = submitText;

    this.screens = UnifiedSignupScreenProvider.buildScreens({
      sessionInfo: this.sessionInfo,
      formDefinition: this.formDefinition,
      onSubmitForm: this.onSubmitForm,
      onUpdatePage: this.onUpdatePage,
      isNextButtonHidden,
      isBackButtonHidden,
      isFormSubmitting,
      signupSurveyDiv,
      submitText: this.submitText,
    });
  }

  public getScreen(): SignupScreen {
    const forcedScreen = this.forceScreen();
    if (forcedScreen) {
      return forcedScreen;
    }

    if (this.sessionInfo.value.isErroredOut) {
      return new ErrorOutSignupScreen({
        formDefinition: this.formDefinition,
        isBackButtonHidden: this.isBackButtonHidden,
        sessionInfo: this.sessionInfo,
      });
    }

    // see if we should suggested a plan
    const planScreen = this.handleShowChoosePlanScreen();
    if (planScreen) {
      return planScreen;
    }

    // see if we will need more data
    const found: Array<SignupScreen> = this.screens.filter((screen) => screen.predicate());
    const screenNames = found.map((s) => s.screenName);
    if (found.length !== 1) {
      const msg = `Invalid number of screens ${found.length} - ${screenNames} - ${JSON.stringify(
        this.sessionInfo.value
      )}`;

      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateUnifiedSignup,
        msg,
      });

      throw new Error(msg);
    }

    console.log('screen name', screenNames);

    return found[0] as SignupScreen;
  }

  /** Called in the constructor  */
  private static buildScreens({
    sessionInfo,
    formDefinition,
    onSubmitForm,
    onUpdatePage,
    isBackButtonHidden,
    isFormSubmitting,
    signupSurveyDiv,
    submitText,
  }: {
    sessionInfo: Ref<UnifiedSignupSessionInfo>;
    formDefinition: Ref<HekateDisplayForm>;
    onSubmitForm: OnSubmitFormFunction;
    onUpdatePage: VoidFunction;
    isNextButtonHidden: Ref<boolean>;
    isBackButtonHidden: Ref<boolean>;
    isFormSubmitting: Ref<boolean>;
    signupSurveyDiv: HTMLDivElement | null;
    submitText: string;
  }): Array<SignupScreen> {
    const pro1 = new ProDetails1SignupScreen({ sessionInfo, formDefinition, isBackButtonHidden });
    const pro2 = new ProDetails2SignupScreen({
      sessionInfo,
      formDefinition,
      isBackButtonHidden,
      onSubmitForm,
      otherScreenComponents: pro1.getVisibleComponents(),
      isFormSubmitting,
      onUpdatePage,
    });
    const free1 = new FreeDetails1SignupScreen({ sessionInfo, formDefinition, isBackButtonHidden });
    const free2 = new FreeDetails2SignupScreen({
      sessionInfo,
      formDefinition,
      isBackButtonHidden,
      onSubmitForm,
      otherScreenComponents: free1.getVisibleComponents(),
      isFormSubmitting,
      onUpdatePage,
    });
    const standard = new StandardDetailsScreen({
      sessionInfo,
      formDefinition,
      isBackButtonHidden,
      onSubmitForm,
      otherScreenComponents: [],
      isFormSubmitting,
      onUpdatePage,
    });
    const eduStudent = new EduDetailsStudentSignupScreen({
      sessionInfo,
      formDefinition,
      isBackButtonHidden,
      onSubmitForm,
      otherScreenComponents: [],
      isFormSubmitting,
      onUpdatePage,
    });
    const eduStudentK12 = new EduDetailsStudentK12SignupScreen({
      sessionInfo,
      formDefinition,
      isBackButtonHidden,
      onSubmitForm,
      otherScreenComponents: [],
      isFormSubmitting,
      onUpdatePage,
    });
    const eduEducator = new EduDetailsEducatorSignupScreen({
      sessionInfo,
      formDefinition,
      isBackButtonHidden,
      onSubmitForm,
      otherScreenComponents: [],
      isFormSubmitting,
      onUpdatePage,
    });

    return [
      pro1,
      pro2,
      free1,
      free2,
      standard,
      eduStudent,
      eduStudentK12,
      eduEducator,
      new GenericStartSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        submitText,
      }),
      new HomePageStartSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        submitText,
      }),
      new HomePageNameSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new TrialStartSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        submitText,
      }),
      new EduStartSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        submitText,
      }),
      new FreeStartSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        submitText,
      }),
      new LandingStartSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        submitText,
      }),
      new StandardStartSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        submitText,
      }),
      new EmailCheck204SignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new ErrorAgeUnderThirteenSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new EmailCheck401SignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
      }),
      new EmailCheck422SignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new EmailCheck500SignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new IntentSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new PersonaBizIntentSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new PersonaHobbyIntentSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new PersonaEduIntentSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new PersonaEduConvergeSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new SignupSuccessSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        signupSurveyDiv,
      }),
      new SignupSuccessRedirectSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new EmailStatusErrorSignupScreen({ sessionInfo, formDefinition, isBackButtonHidden }),
      new SignupSubmittingSignupScreen({
        formDefinition,
        isBackButtonHidden,
        sessionInfo,
      }),
      new EmailStatusSignupScreen({
        sessionInfo,
        formDefinition,
        isBackButtonHidden,
        signupSurveyDiv,
      }),
    ];
  }

  /**
   * return the screen if we should show it otherwise null.
   * Also sets the plan in sessionInfo if we can/should skip the choose step.
   */
  private handleShowChoosePlanScreen(): ChoosePlanSignupScreen | null {
    const {
      startScreen,
      emailType,
      intent,
      persona,
      isChooseOwnPlan,
      chosenPlan,
      isEduRenew,
      isUnderThirteen,
    } = this.sessionInfo.value;

    if (chosenPlan !== null) {
      return null;
    }
    if (emailType === null || startScreen === null || intent === null || persona === null) {
      return null;
    }

    if (isUnderThirteen) {
      return null;
    }

    if (isChooseOwnPlan) {
      if (!UnifiedSignupChoosePlanCards.seeOtherOptions.screenName) {
        throw new Error('missing see other options screen name');
      }
      return new ChoosePlanSignupScreen({
        sessionInfo: this.sessionInfo,
        formDefinition: this.formDefinition,
        screenName: UnifiedSignupChoosePlanCards.seeOtherOptions.screenName,
        onUpdatePage: this.onUpdatePage,
        isBackButtonHidden: this.isBackButtonHidden,
      });
    }
    const planCards = UnifiedSignupChoosePlanCards.getInstance({
      startScreen,
      emailType,
      intent,
      persona,
      isEduRenew,
    });

    if (planCards.plan !== null) {
      this.sessionInfo.value.chosenPlan = planCards.plan;
      return null;
    }
    if (planCards.screenName === null) {
      throw ErrorUtil.internalError({ msg: 'planCards screenName unknown' });
    }

    return new ChoosePlanSignupScreen({
      sessionInfo: this.sessionInfo,
      formDefinition: this.formDefinition,
      screenName: planCards.screenName,
      onUpdatePage: this.onUpdatePage,
      isBackButtonHidden: this.isBackButtonHidden,
    });
  }

  private forceScreen(): SignupScreen | null {
    const { emailCheckCode } = this.sessionInfo.value;
    if (!emailCheckCode) return null;

    try {
      const forcedScreen = UnifiedSignupUtil.getForcedScreenOrNull();
      if (!forcedScreen) return null;

      document.querySelector('body')?.classList.add('hekate-uss');

      console.warn(`Forcing Screen ${forcedScreen}`);

      const foundScreen = this.screens.filter((screen) => screen.screenName === forcedScreen);
      if (foundScreen.length === 1) return foundScreen[0] || null;

      if (forcedScreen === 'uss-error-out') {
        return new ErrorOutSignupScreen({
          formDefinition: this.formDefinition,
          isBackButtonHidden: this.isBackButtonHidden,
          sessionInfo: this.sessionInfo,
        });
      }

      const foundPlanCards = UnifiedSignupChoosePlanCards.findByName(forcedScreen);
      if (foundPlanCards && foundPlanCards.screenName) {
        return new ChoosePlanSignupScreen({
          sessionInfo: this.sessionInfo,
          formDefinition: this.formDefinition,
          screenName: foundPlanCards.screenName,
          onUpdatePage: this.onUpdatePage,
          isBackButtonHidden: this.isBackButtonHidden,
        });
      }
    } catch {
      /** ignore */
    }

    return null;
  }
}
